import type { ReactNode } from 'react';
import * as Primitive from '@radix-ui/react-tooltip';

import { cn, POPOVER_MOTION } from '../lib/cn';

export const TooltipProvider = ({ children }: { children: ReactNode }) => (
  <Primitive.Provider delayDuration={200} skipDelayDuration={400}>
    {children}
  </Primitive.Provider>
);

interface ITooltipProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
}

export const Tooltip = ({ content, children, side = 'top' }: ITooltipProps) => (
  <Primitive.Root>
    <Primitive.Trigger asChild>{children}</Primitive.Trigger>
    <Primitive.Portal>
      <Primitive.Content
        side={side}
        sideOffset={6}
        collisionPadding={8}
        className={cn(
          'z-50 max-w-tooltip rounded-panel bg-tooltip px-3 py-inset shadow-panel',
          'border border-border-raised text-meta leading-17 text-muted',
          POPOVER_MOTION,
        )}
      >
        {content}
      </Primitive.Content>
    </Primitive.Portal>
  </Primitive.Root>
);

interface IShortcutTooltipProps {
  label: ReactNode;
  keys?: string;
  children: ReactNode;
  side?: ITooltipProps['side'];
  /** The name is printed beside the trigger already (an expanded sidebar): no tip at all. */
  off?: boolean;
}

// Board 27's rail tip: a name and its key, in the text colour, not the muted hint above.
export const ShortcutTooltip = ({
  label,
  keys,
  children,
  side = 'right',
  off,
}: IShortcutTooltipProps) =>
  off ? (
    children
  ) : (
    <Primitive.Root>
      <Primitive.Trigger asChild>{children}</Primitive.Trigger>
      <Primitive.Portal>
        <Primitive.Content
          side={side}
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            'z-50 flex items-center gap-2 rounded-control bg-chip-on px-inset py-label shadow-panel',
            'text-meta font-medium whitespace-nowrap text-primary',
            POPOVER_MOTION,
          )}
        >
          {label}
          {keys && (
            <kbd className="rounded-tag border border-suppressed px-tight py-tight font-mono text-meta font-medium text-muted">
              {keys}
            </kbd>
          )}
        </Primitive.Content>
      </Primitive.Portal>
    </Primitive.Root>
  );

export const InfoDot = ({
  children,
  side,
}: {
  children: ReactNode;
  side?: ITooltipProps['side'];
}) => (
  <Tooltip content={children} side={side}>
    <button
      type="button"
      aria-label="Why this matters"
      className={cn(
        'inline-flex size-info shrink-0 items-center justify-center rounded-full',
        'border border-suppressed text-info font-semibold text-faint',
        'hover:border-border-strong hover:text-muted',
        'focus:ring-3 focus:ring-focus focus:outline-none',
      )}
    >
      i
    </button>
  </Tooltip>
);
